import { fixedChargeEditVariantsListItemSelectors,
  fixedChargesHeaderEditSelectors,
  getSelectorAttribute } from '@weft/app-selectors';
import type { InstallmentsProduct } from '@weft/services';
import { isNullOrUndefined, removeInIndex } from '@weft/utils';
import cn from 'classnames';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { Input } from '../../../../../../../components/design/Input';
import Toggle from '../../../../../../../components/design/toggle';
import { TrashIcon } from '../../../../../../../components/icons/TrashIcon';
import { useCreatePlanContext } from '../../../../../../../contexts/create-plan-context';
import { useScrollIntoViewOnMount } from '../../../../../../../hooks/use-scroll-to';
import { vyFormRegister } from '../../../../../../../utils/react-hook-forms-utils';
import { useChargeContext } from '../../../charge-context';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { PlanChargesContainer } from '../../../PlanChargesContainer';

export const InstallmentsChargeEdit: React.FC = () => {
  const { item: product, itemIndex: productIndex } = useChargeContext<InstallmentsProduct>();
  const { setValue, getValues, register, getFieldState } = useFormContext<PlanInputForm>();
  const { formUtils: { isFieldEditable } } = useCreatePlanContext();

  useScrollIntoViewOnMount(`create-product-${productIndex}`);

  const nameRegister = vyFormRegister(register, `fixedProducts.${productIndex}.variants.0.name`);
  const descriptionRegister = vyFormRegister(register, `fixedProducts.${productIndex}.variants.0.description`);

  const installmentsRegister = register(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.installments`, {
    setValueAs: (installments: string) => isNullOrUndefined(installments) ? undefined : parseInt(installments),
    valueAsNumber: true,
  });

  const feeRegister = register(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.price`, {
    setValueAs: (price: string) => isNullOrUndefined(price) ? undefined : parseFloat(price),
    valueAsNumber: true,
  });

  const onDeleteClicked = () => {
    const updatedProducts = removeInIndex(getValues('fixedProducts'), productIndex);
    setValue('fixedProducts', updatedProducts);
  };

  const onProRataChanged = (checked: boolean) => {
    setValue(`fixedProducts.${productIndex}.proRata`, checked);
  };

  const isProductEditable = isFieldEditable(`fixedProducts.${productIndex}`);

  return (
    <PlanChargesContainer
      key={product.id}
      className="pt-6"
      id={`create-product-${productIndex}`}
    >
      <div className='divide-line space-y-8 divide-y'>
        <div
          className={cn({ 'opacity-70': !isFieldEditable(`fixedProducts.${productIndex}`) })}
        >
          <div className={twMerge('divide-line border-line gap-y-6 flex flex-col border-b pb-5 lg:flex-none',
            !isProductEditable && 'opacity-40')}
          >
            <div className="flex gap-x-6">
              <div className="flex w-full min-w-[150px] flex-1">
                <Input
                  {...getSelectorAttribute(fixedChargesHeaderEditSelectors.nameInput)}
                  label='Service name'
                  placeholder='Text here'
                  {...nameRegister}
                  defaultValue={getValues(nameRegister.name)}
                  className={cn({ 'border border-input-error': getFieldState(nameRegister.name).invalid })}
                  errorMessage={getFieldState(nameRegister.name).error?.message}
                  disabled={!isProductEditable}
                />
              </div>

              <div className="flex w-full min-w-[150px] flex-1">
                <Input
                  {...getSelectorAttribute(fixedChargesHeaderEditSelectors.descriptionInput)}
                  label='Description'
                  placeholder='Add description here'
                  {...descriptionRegister}
                  defaultValue={getValues(descriptionRegister.name) ?? undefined}
                  className={cn({ 'border border-input-error': getFieldState(descriptionRegister.name).invalid })}
                  errorMessage={getFieldState(descriptionRegister.name).error?.message}
                  disabled={!isProductEditable}
                />
              </div>
            </div>

            <div className="flex gap-x-6">
              <div className="flex w-full min-w-[150px] flex-1">
                <Input
                  label='Installments'
                  placeholder='Number of installments'
                  {...installmentsRegister}
                  defaultValue={getValues(installmentsRegister.name)}
                  className={cn({ 'border border-input-error': getFieldState(installmentsRegister.name).invalid })}
                  errorMessage={getFieldState(installmentsRegister.name).error?.message}
                  type='number'
                  disabled={!isProductEditable}
                />
              </div>

              <div className="flex w-full min-w-[150px] flex-1">
                <Input
                  {...getSelectorAttribute(fixedChargesHeaderEditSelectors.amountInput)}
                  label='Fee'
                  placeholder='$'
                  {...feeRegister}
                  defaultValue={getValues(feeRegister.name)}
                  className={cn({ 'border border-input-error': getFieldState(feeRegister.name).invalid })}
                  errorMessage={getFieldState(feeRegister.name).error?.message}
                  step="any"
                  type='number'
                  disabled={!isProductEditable}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pb-3.5 pt-4">
            <div className="flex items-center gap-3">
              <div className='flex items-center gap-3'>
                <p className="text-label">Pro Rata</p>
                <Toggle
                  switchClassName='translate-y-1'
                  checked={getValues(`fixedProducts.${productIndex}.proRata`)}
                  onToggle={checked => onProRataChanged(checked)}
                  disabled={!isProductEditable}
                />
              </div>
            </div>

            {isProductEditable && (
              <TrashIcon
                {...getSelectorAttribute(fixedChargeEditVariantsListItemSelectors.trashButton)}
                onClick={onDeleteClicked}
                className='fill-icon'
              />
            )}
          </div>
        </div>
      </div>
    </PlanChargesContainer>
  );
};